import React from 'react';
import { Box } from '@mui/material';
import FieldRow from './FieldRow';
import LocalizedField from './LocalizedField';
import SectionBlock from './SectionBlock';
import { fmtLocalDate as fmtDate } from '../../../utils/dateUtils';

export default function ItemDetailPanel({ data, diffKeys = new Set(), side }) {
  if (!data) return null;

  // ItemPriceOut only has { baseCost (minor units), currencyId } — no symbol/decimals.
  const prices    = Array.isArray(data.prices)    ? data.prices    : [];
  const discounts = Array.isArray(data.discounts) ? data.discounts : [];
  const merch     = data.merchandise;
  const ticket    = data.ticket;
  const badge     = data.badge;
  const avatar    = data.avatar;
  const tax       = data.tax;

  // Tax mode: top-level taxIsInclusive (from DTO merge) takes priority over nested tax.isInclusive.
  const taxModeStr = (() => {
    const v = data.taxIsInclusive !== undefined ? data.taxIsInclusive : tax?.isInclusive;
    return v == null ? null : (v ? 'Inclusive' : 'Exclusive');
  })();

  return (
    <Box>
      <SectionBlock title="Identity" />
      <LocalizedField
        label="Name"
        fieldKey="nameLocalizations"
        data={data.nameLocalizations}
        diffKeys={diffKeys}
        side={side}
      />
      <LocalizedField
        label="Description"
        fieldKey="descriptionLocalizations"
        data={data.descriptionLocalizations}
        diffKeys={diffKeys}
        side={side}
      />
      <FieldRow
        label="Item Type"
        value={data.itemTypeId != null ? String(data.itemTypeId) : null}
        isChanged={diffKeys.has('itemTypeId')}
        side={side}
      />
      <FieldRow
        label="Available"
        value={data.isAvailable == null ? null : (data.isAvailable ? 'Yes' : 'No')}
        isChanged={diffKeys.has('isAvailable')}
        side={side}
      />

      {prices.length > 0 && (
        <>
          <SectionBlock title="Pricing" />
          {prices.map((p, i) => (
            <FieldRow
              key={p.currencyId ?? i}
              label={`Price (currency ${p.currencyId ?? i + 1})`}
              value={p.baseCost != null ? String(p.baseCost) : null}
              isChanged={diffKeys.has('prices')}
              side={side}
            />
          ))}
        </>
      )}

      {(tax != null || data.taxIsInclusive !== undefined) && (
        <>
          <SectionBlock title="Tax" />
          <FieldRow
            label="Tax Rate"
            value={tax?.ratePct != null ? `${tax.ratePct}%` : null}
            isChanged={diffKeys.has('tax')}
            side={side}
          />
          <FieldRow
            label="Tax Mode"
            value={taxModeStr}
            isChanged={diffKeys.has('tax') || diffKeys.has('taxIsInclusive')}
            side={side}
          />
        </>
      )}

      {merch != null && (
        <>
          <SectionBlock title="Merchandise" />
          <FieldRow
            label="Merch Type"
            value={merch.merchandiseTypeName ?? (merch.merchandiseTypeId != null ? String(merch.merchandiseTypeId) : null)}
            isChanged={diffKeys.has('merchandise')}
            side={side}
          />
          <FieldRow
            label="Category"
            value={merch.merchandiseCategoryName ?? (merch.merchandiseCategoryId != null ? String(merch.merchandiseCategoryId) : null)}
            isChanged={diffKeys.has('merchandise')}
            side={side}
          />
          <FieldRow
            label="Position"
            value={merch.position ?? null}
            isChanged={diffKeys.has('merchandise')}
            side={side}
          />
          <FieldRow
            label="Interaction"
            value={merch.interaction ?? null}
            isChanged={diffKeys.has('merchandise')}
            side={side}
          />
          <FieldRow
            label="Asset URL"
            value={merch.assetUrl ?? null}
            isChanged={diffKeys.has('merchandise')}
            side={side}
          />
          {(merch.thumbnail?.linkLocalizations ?? []).map((entry) => (
            <FieldRow
              key={entry.localeId ?? 'thumb'}
              label={`Thumbnail (${(entry.localeId ?? '').slice(0, 2).toUpperCase() || '?'})`}
              value={entry.mediaLink ?? '—'}
              isChanged={diffKeys.has('merchandise')}
              side={side}
            />
          ))}
        </>
      )}

      {ticket != null && (
        <>
          <SectionBlock title="Ticket" />
          <FieldRow
            label="Ticket Type"
            value={ticket.ticketTypeId != null ? String(ticket.ticketTypeId) : null}
            isChanged={diffKeys.has('ticket')}
            side={side}
          />
          <FieldRow
            label="Event ID"
            value={ticket.eventId != null ? String(ticket.eventId) : null}
            isChanged={diffKeys.has('ticket')}
            side={side}
          />
          <FieldRow
            label="Seat"
            value={ticket.seat ?? null}
            isChanged={diffKeys.has('ticket')}
            side={side}
          />
          <FieldRow
            label="Section"
            value={ticket.section ?? null}
            isChanged={diffKeys.has('ticket')}
            side={side}
          />
          <FieldRow
            label="Valid From"
            value={fmtDate(ticket.validFrom)}
            isChanged={diffKeys.has('ticket')}
            side={side}
          />
          <FieldRow
            label="Valid To"
            value={fmtDate(ticket.validTo)}
            isChanged={diffKeys.has('ticket')}
            side={side}
          />
          <FieldRow
            label="Transferable"
            value={ticket.isTransferable == null ? null : (ticket.isTransferable ? 'Yes' : 'No')}
            isChanged={diffKeys.has('ticket')}
            side={side}
          />
        </>
      )}

      {badge != null && (
        <>
          <SectionBlock title="Badge" />
          {(badge.media?.linkLocalizations ?? []).map((entry) => (
            <FieldRow
              key={entry.localeId ?? 'badge'}
              label={`Badge Image (${(entry.localeId ?? '').slice(0, 2).toUpperCase() || '?'})`}
              value={entry.mediaLink ?? '—'}
              isChanged={diffKeys.has('badge')}
              side={side}
            />
          ))}
        </>
      )}

      {avatar != null && (
        <>
          <SectionBlock title="Avatar" />
          <FieldRow
            label="Asset URL"
            value={avatar.assetUrl ?? null}
            isChanged={diffKeys.has('avatar')}
            side={side}
          />
          {(avatar.thumbnail?.linkLocalizations ?? []).map((entry) => (
            <FieldRow
              key={entry.localeId ?? 'avatar-thumb'}
              label={`Thumbnail (${(entry.localeId ?? '').slice(0, 2).toUpperCase() || '?'})`}
              value={entry.mediaLink ?? '—'}
              isChanged={diffKeys.has('avatar')}
              side={side}
            />
          ))}
        </>
      )}

      {discounts.length > 0 && (
        <>
          <SectionBlock title="Discounts" />
          {discounts.map((d, i) => {
            const typeId = d.discountTypeId;
            const label  = `Discount ${i + 1}${typeId != null ? ` (type ${typeId})` : ''}`;
            const value  = d.ratePct != null
              ? `${d.ratePct}% off`
              : d.amount != null
                ? `${d.amount} flat off`
                : '—';
            return (
              <FieldRow
                key={i}
                label={label}
                value={value}
                isChanged={diffKeys.has('discounts')}
                side={side}
              />
            );
          })}
        </>
      )}
    </Box>
  );
}
